import {
  applyNodeChanges,
  Node as ReactFlowNode,
  NodeChange,
  NodeDimensionChange,
  NodePositionChange,
  useNodesInitialized,
  useOnViewportChange,
  useReactFlow,
  useStoreApi,
  Viewport,
} from '@xyflow/react';
import { compact } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { INTER_SECTION_NODE_PADDING } from '@/components/diagrams/FlowChart/constants';
import {
  isNodeAwaitingGraveyardLayout,
  NODE_GRAVEYARD_Y_OFFSET,
} from '@/modules/graphViz/graphVizNodeConfig/nodeGraveyard.utils';
import { assertNonNil } from '@/utils/assertUtils';

import { useFlowChartContext } from '../../context/flowChart.context';
import { useCanvasDimensions } from '../../hooks/useCanvasDimensions';
import { ExternalFlowChartProps, Node } from '../../types';
import {
  getBoundingBoxForNodes,
  isSectionLabelChildNode,
  isSectionLabelNode,
} from '../../utils/nodes';

/**
 * Estate-Waterfall Context – Section Label Layout Hooks
 * ----------------------------------------------------
 * A single *estate waterfall* diagram can contain many *sections* (think of them
 * as discrete phases or tiers in the distribution waterfall).  Each section is
 * visualised by a *section label* node that floats above its child nodes.
 *
 * These hooks keep the visual contract intact:
 *   • The label always sits `padding` px above the tallest child in its
 *     section and spans the full width of the viewport.
 *   • Drag behavior has two distinct modes:
 *     1. SINGLE-SECTION DRAG: The layout remains fluid and reactive. As one
 *        section grows/shrinks, others cascade up/down to maintain padding.
 *        This maintains visual coherence during normal editing.
 *     2. MULTI-SECTION DRAG: When nodes in multiple sections are dragged
 *        simultaneously, we suspend the cascade mechanism to prevent feedback
 *        loops. Sections are visually greyed-out to indicate this special mode.
 *   • When the viewport pans / zooms we recompute widths (but *never* touch
 *     Y positions so we avoid feedback loops).
 *
 * The core of the work happens in `createSectionBoundsHandlers`, which offers
 * two imperative helpers: `initSectionBounds` (one-time initialisation) and
 * `computeDiff` (called on every node/viewport change that might influence
 * vertical layout).
 */

interface CreateSectionBoundsHandlersInput {
  changes: NodeChange[];
  nextNodes: Node[];
  padding?: number;
  /**
   * Gap between a section's label and its first tile. Accepts either a
   * single number (uniform across all sections) or an array indexed by
   * section document order. Out-of-range indices fall back to the last
   * array entry.
   */
  labelPadding?: number | number[];
  /**
   * Gap between the bottom of one section's content and the next section's
   * label. Accepts either a single number (uniform across all gaps) or an
   * array indexed by gap — gap `i` is the space between section `i` and
   * section `i+1` in document order. Out-of-range indices fall back to the
   * last array entry.
   */
  interSectionPadding?: number | number[];
  graveyardPadding?: number;
}

export function createSectionBoundsHandlers({
  changes,
  nextNodes,
  padding = 50,
  labelPadding: labelPaddingOverride,
  interSectionPadding: interSectionPaddingOverride,
  graveyardPadding: graveyardPaddingOverride,
}: CreateSectionBoundsHandlersInput) {
  /** Gap between a *section label* and the tallest child inside that section */
  const labelPadding = labelPaddingOverride ?? padding;

  /** Gap between the bottom of one section's content and the next section's label */
  const interSectionPadding = interSectionPaddingOverride ?? padding;

  /**
   * Resolves per-gap inter-section padding. `gapIdx` is the index of the
   * gap between section `gapIdx` and section `gapIdx+1` in document order.
   * When the input is a number, returns it for every gap; when it's an
   * array, returns `interSectionPadding[gapIdx]` with fallback to the last
   * entry (or `padding`) for safety.
   */
  function getInterSectionPaddingAt(gapIdx: number): number {
    if (typeof interSectionPadding === 'number') {
      return interSectionPadding;
    }
    if (interSectionPadding.length === 0) {
      return padding;
    }
    return (
      interSectionPadding[gapIdx] ??
      interSectionPadding[interSectionPadding.length - 1] ??
      padding
    );
  }

  /**
   * Resolves the label padding for a given section by its document-order
   * index. Scalar input applies uniformly; array input is indexed by
   * section, with fallback to the last entry (or `padding`) for safety.
   */
  function getLabelPaddingAt(sectionIdx: number): number {
    if (typeof labelPadding === 'number') {
      return labelPadding;
    }
    if (labelPadding.length === 0) {
      return padding;
    }
    return (
      labelPadding[sectionIdx] ??
      labelPadding[labelPadding.length - 1] ??
      padding
    );
  }

  /**
   * Resolves the label padding for a section by its ID. Looks up the
   * document-order index via {@link getSectionNodes} — cheap because
   * section counts are small.
   */
  function getLabelPaddingForSection(sectionNodeId: string): number {
    const sectionIdx = getSectionNodes().findIndex(
      (n) => n.id === sectionNodeId
    );
    return getLabelPaddingAt(sectionIdx >= 0 ? sectionIdx : 0);
  }

  /** Gap used when positioning *graveyard* nodes above the section content */
  const graveyardPadding = graveyardPaddingOverride ?? padding;

  function getMutableNode(id: string): Node {
    return assertNonNil(
      nextNodes.find((n) => n.id === id),
      `could not find node ${id}`
    );
  }

  function getSectionNodes() {
    return nextNodes.filter(isSectionLabelNode);
  }

  function getSectionChildren(id: string) {
    return nextNodes.filter(
      (n) => isSectionLabelChildNode(n) && n.data.sectionLabelId === id
    );
  }

  function getBoundsForSectionChildren(sectionNodeId: string) {
    const graveyardNodesForSection = getSectionChildren(sectionNodeId).filter(
      isNodeAwaitingGraveyardLayout
    );
    const nonGraveyardNodesForSection = getSectionChildren(
      sectionNodeId
    ).filter((n) => !isNodeAwaitingGraveyardLayout(n));

    if (nonGraveyardNodesForSection.length === 0) {
      nonGraveyardNodesForSection.push(...graveyardNodesForSection);
    }

    return getBoundingBoxForNodes(nonGraveyardNodesForSection);
  }

  function syncSectionNodeTopToChildren(sectionNodeId: string) {
    const boundsForSectionChildren = getBoundsForSectionChildren(sectionNodeId);
    const graveyardNodesForSection = getSectionChildren(sectionNodeId).filter(
      isNodeAwaitingGraveyardLayout
    );

    // Modify the node graveyard nodes manually to sit above the top of the section nodes
    graveyardNodesForSection
      .map((n) => getMutableNode(n.id))
      .forEach((n) => {
        n.position.y =
          boundsForSectionChildren.top -
          NODE_GRAVEYARD_Y_OFFSET -
          graveyardPadding;
      });

    getMutableNode(sectionNodeId).position.y =
      boundsForSectionChildren.top - getLabelPaddingForSection(sectionNodeId);
  }

  function addYDiffToSectionChildren(sectionNodeId: string, diff: number) {
    getSectionChildren(sectionNodeId).forEach((n) => {
      getMutableNode(n.id).position.y += diff;
    });
  }

  function initSectionNodeTopBounds() {
    getSectionNodes().forEach((sectionNode) => {
      syncSectionNodeTopToChildren(sectionNode.id);
    });
  }

  function forEachNode<T extends Node>(
    nodes: T[],
    cb: (current: T, prev?: T, next?: T) => void
  ) {
    nodes.forEach((n, i) => cb(n, nodes[i - 1], nodes[i + 1]));
  }

  /**
   * Computes the diff for the "current section", i.e. the one that had a node inside it change
   * position or dimensions
   */
  function computeCurrentSectionDiff() {
    // 1. Traverse in reverse needed for pulling up the bottom of each section
    const orderedSectionNodes = getSectionNodes();
    const sectionNodes = orderedSectionNodes.slice().reverse();
    forEachNode(sectionNodes, (sectionNode, prevSectionNode) => {
      // 2. Sync node bounds with prev section (bottom line)
      if (prevSectionNode) {
        const { bottom } = getBoundsForSectionChildren(sectionNode.id);
        // sectionNode is the upper section of the pair (prevSectionNode is
        // the one below in document order, since we're iterating reversed).
        // The gap index is the document-order index of the upper section.
        const gapIdx = orderedSectionNodes.indexOf(sectionNode);
        const yDiff =
          prevSectionNode.position.y -
          bottom -
          getInterSectionPaddingAt(gapIdx);
        addYDiffToSectionChildren(sectionNode.id, yDiff);
      }

      // 3. Re-sync the top
      syncSectionNodeTopToChildren(sectionNode.id);
    });
  }

  /**
   * 1. Sync the top node bounds to top of each node
   * 2. "Pull up" each section so that there is appropriate padding between the sections
   */
  function initSectionBounds() {
    initSectionNodeTopBounds();
    computeCurrentSectionDiff();
  }

  // ------------------------------------------------------------------
  // computeDiff – live reconciliation
  // ------------------------------------------------------------------
  // This runs *many* times during a drag.  We therefore keep the algorithm
  // single-pass where possible and bail out early in known cheap cases.
  // The objective is two-fold:
  //   1. Maintain intra-section consistency (label above children).
  //   2. Maintain inter-section spacing (cascade with uniform padding).
  //
  // IMPORTANT: There are two fundamentally different behaviors:
  // - Single-Section Drag: Full cascading with continuous repositioning
  //   of subsequent sections to maintain proper spacing and visual hierarchy.
  // - Multi-Section Drag: Cascading is suspended to avoid feedback loops.
  //   Each section positions its label above its children independently,
  //   sections are greyed out to indicate the special mode.
  // ------------------------------------------------------------------
  function computeDiff() {
    const isDragging = changes.some((c) => c.type === 'position' && c.dragging);

    // Identify all sections that contain nodes currently being dragged.
    const draggingSectionIds = new Set<string>();

    if (isDragging) {
      changes.forEach((c) => {
        if (c.type !== 'position' || !c.dragging) {
          return;
        }

        const node = nextNodes.find((n) => n.id === c.id);
        if (!node) return;

        if (isSectionLabelNode(node)) {
          draggingSectionIds.add(node.id);
        } else if (isSectionLabelChildNode(node) && node.data.sectionLabelId) {
          draggingSectionIds.add(node.data.sectionLabelId);
        }
      });
    }

    // Check if we're in a multi-section drag scenario
    const isMultiSectionDrag = draggingSectionIds.size > 1;

    // Apply greying effect reactively based on multi-drag state
    getSectionNodes().forEach((sectionNode) => {
      // Grey out all section nodes during multi-section drag
      // Normal opacity otherwise
      const node = getMutableNode(sectionNode.id);
      node.style = {
        opacity: isMultiSectionDrag ? 0.5 : 1,
      };
    });

    // If multiple sections are being dragged simultaneously, update section labels
    // to match bounds of their contained elements, but skip cascade adjustments
    if (isMultiSectionDrag) {
      // Update each section label to match the bounds of its children
      // without cascading changes that would cause jitter
      const topPerSectionNode: [string, number][] = getSectionNodes().map(
        (sectionNode, idx) => {
          const boundsForChildren = getBoundsForSectionChildren(sectionNode.id);
          return [
            sectionNode.id,
            boundsForChildren.top - getLabelPaddingAt(idx),
          ];
        }
      );

      topPerSectionNode.forEach(([id, top]) => {
        // Only update the edge label position to match the top of its contained elements
        // This keeps the visual connection between labels and content
        getMutableNode(id).position.y = top;
      });

      const sectionNodes = getSectionNodes();

      const bottomPerSectionNode: [string, number][] = compact(
        getSectionNodes().map((sectionNode, idx) => {
          const nextSectionNode = sectionNodes[idx + 1];
          if (nextSectionNode) {
            const boundsForChildren = getBoundsForSectionChildren(
              sectionNode.id
            );
            return [
              nextSectionNode.id,
              boundsForChildren.bottom + getInterSectionPaddingAt(idx),
            ];
          }

          return null;
        })
      );

      bottomPerSectionNode.forEach(([id, bottom]) => {
        // Position section labels to appear after the previous section's content (with padding)
        // This creates a cascading layout where each section starts after the previous one ends
        getMutableNode(id).position.y = bottom;
      });
    }

    // For single-section drags or no drag, continue with normal logic
    if (isDragging && !isMultiSectionDrag) {
      // For single-section drags, keep previous behavior using the first entry.
      const currentSectionId = Array.from(draggingSectionIds)[0];

      // Document order is the key the padding array is indexed by (gap `i`
      // = between doc-order section `i` and `i+1`). Keep a lookup so we can
      // translate sorted-by-Y positions back to doc-order when resolving
      // per-gap padding — otherwise an in-flight drag that reorders sections
      // visually would apply the wrong gap's padding to each pair.
      const docOrderedSections = getSectionNodes();
      const docIdxById = new Map(
        docOrderedSections.map((s, i) => [s.id, i] as const)
      );

      // Sort sections top → bottom for deterministic cascading logic.
      const sectionNodes = [...docOrderedSections].sort(
        (a, b) => a.position.y - b.position.y
      );

      let cumulativeBottomWithPadding: number | null = null;

      sectionNodes.forEach((sectionNode, idx) => {
        const { top, bottom } = getBoundsForSectionChildren(sectionNode.id);

        // Desired label Y is always top - padding. Label padding is keyed
        // by doc-order index so the per-section value tracks the section
        // identity rather than its current visual (sorted) position.
        const sectionDocIdx = docIdxById.get(sectionNode.id) ?? idx;
        const sectionLabelPadding = getLabelPaddingAt(sectionDocIdx);
        let desiredLabelY = top - sectionLabelPadding;

        if (sectionNode.id === currentSectionId) {
          // For the actively dragged section we pin the label and let the user
          // control its movement; we don't shift its children.
          sectionNode.position.y = desiredLabelY;
        } else if (cumulativeBottomWithPadding !== null) {
          // Calculate how far we need to move this section (positive -> move
          // down, negative -> move up) so that its label sits exactly at
          // cumulativeBottomWithPadding.
          const diff = cumulativeBottomWithPadding - desiredLabelY;
          if (diff !== 0) {
            addYDiffToSectionChildren(sectionNode.id, diff);
            sectionNode.position.y += diff;
            desiredLabelY += diff;
          }
        } else {
          // First section and not the current; just set label to desired.
          sectionNode.position.y = desiredLabelY;
        }

        // Update cumulative bottom for next iteration. We need to account for
        // any shift applied to this section (captured in `desiredLabelY`). The
        // distance between `top` and `bottom` remains constant for a section
        // because we move children together, so we can compute new bottom by
        // offsetting with (desiredLabelY - (top - padding)).
        const offsetApplied = desiredLabelY - (top - sectionLabelPadding);
        const shiftedBottom = bottom + offsetApplied;

        // Gap between `sectionNode` (upper in sort order) and the next
        // sorted section. The padding array is keyed by the doc-order idx
        // of the upper section — matches `computeCurrentSectionDiff` and
        // stays correct when a drag reorders sections visually.
        cumulativeBottomWithPadding =
          shiftedBottom + getInterSectionPaddingAt(sectionDocIdx);
      });

      // SECOND PASS (bottom → top): ensure preceding sections sit just above
      // the one below them with correct padding. This handles cases where the
      // current section is shrinking/moving upward while dragging, requiring
      // sections above to be pulled up.

      let cumulativeTopWithPadding: number | null = null;
      for (let rIdx = sectionNodes.length - 1; rIdx >= 0; rIdx -= 1) {
        const sectionNode = sectionNodes[rIdx]!;
        const { bottom } = getBoundsForSectionChildren(sectionNode.id);

        // Desired bottom is top + height; we already have bottom.
        let desiredBottom = bottom;

        if (cumulativeTopWithPadding !== null) {
          // We want this section's bottom to be no lower than
          // (nextSectionLabelY - padding). Padding is keyed by doc-order
          // idx of the upper section (this one) — stays correct during a
          // drag even when sort order diverges from doc order.
          const gapIdx = docIdxById.get(sectionNode.id) ?? rIdx;
          const allowedBottom =
            cumulativeTopWithPadding - getInterSectionPaddingAt(gapIdx);
          const diff = allowedBottom - desiredBottom; // negative diff -> move up

          // Pull the entire section up.
          addYDiffToSectionChildren(sectionNode.id, diff);
          sectionNode.position.y += diff;
          desiredBottom += diff;
        }

        // Update cumulative top for the next iteration (moving upwards)
        cumulativeTopWithPadding = sectionNode.position.y;
      }
    } else if (!isDragging) {
      // If not dragging at all, initialize section bounds
      initSectionBounds();
    }
  }

  return { initSectionBounds, computeDiff };
}

/**
 * Hook – useSectionNodesBounds
 * Bridges the React Flow *node-change* stream with our imperative diff logic.
 * It decorates the `applyNodeChanges` reducer so that every batch gets run
 * through `createSectionBoundsHandlers`.  Importantly, we short-circuit when
 * the batch is a pure *viewport-width* update to prevent feedback loops.
 */
export interface UseSectionNodesBoundsOpts {
  padding?: number;
  /** See {@link CreateSectionBoundsHandlersInput.labelPadding}. */
  labelPadding?: number | number[];
  /** See {@link CreateSectionBoundsHandlersInput.interSectionPadding}. */
  interSectionPadding?: number | number[];
}

/**
 * @description Computes and initializes section node bounds
 * as changes in dimension / position of child nodes are made by the user
 */
export function useSectionNodesBounds({
  padding,
  labelPadding,
  interSectionPadding = INTER_SECTION_NODE_PADDING,
}: UseSectionNodesBoundsOpts = {}) {
  const { setNodes, getNodes } = useReactFlow();
  const store = useStoreApi();
  const isInitialized = useNodesInitialized();
  const [didRunInit, setDidRunInit] = useState(false);

  const initSectionBounds = useCallback((): void => {
    const nextNodes = getNodes() as Node[];
    const { initSectionBounds: init } = createSectionBoundsHandlers({
      changes: [],
      nextNodes,
      padding,
      labelPadding,
      interSectionPadding,
    });

    init();
    setNodes(nextNodes);
  }, [getNodes, interSectionPadding, labelPadding, padding, setNodes]);

  const applySectionBoundsToNodes = useCallback(
    (changes: NodeChange[], nodes: Node[]): Node[] => {
      const nextNodes = applyNodeChanges(
        changes,
        nodes as ReactFlowNode[]
      ) as Node[];

      // If not initialized, nodes don't have a proper width/height to do any computations with
      // so we can return them right away
      if (!isInitialized) return nextNodes;

      // ------------------------------------------------------------------
      // Ignore pure "viewport width" batches.
      // ------------------------------------------------------------------
      // These are synthetic node-change batches we fire from
      // `useSectionNodesResizeHandlers` to keep labels full-width while the
      // user pans / zooms. They never alter Y/height, so we short-circuit to
      // avoid triggering vertical re-layout.
      // ------------------------------------------------------------------

      const isPureViewportWidthBatch = ((): boolean => {
        // Must include at least one width change.
        if (!changes.some((c) => c.type === 'dimensions')) return false;

        return changes.every((c) => {
          const changeId = (c as { id?: string }).id;
          if (!changeId) return false;
          const prev = nodes.find((n) => n.id === changeId);
          if (!prev) return false;

          if (c.type === 'dimensions') {
            return (c.dimensions?.height ?? prev.height) === prev.height;
          }

          if (c.type === 'position') {
            return c.position?.y === prev.position.y;
          }

          return false; // unexpected change type
        });
      })();

      if (isPureViewportWidthBatch) {
        return nextNodes;
      }

      if (!didRunInit) {
        const { initSectionBounds } = createSectionBoundsHandlers({
          changes,
          nextNodes,
          padding,
          labelPadding,
          interSectionPadding,
        });
        initSectionBounds();
        setDidRunInit(true);
      } else {
        const { computeDiff } = createSectionBoundsHandlers({
          changes,
          nextNodes,
          padding,
          labelPadding,
          interSectionPadding,
        });
        computeDiff();
      }

      // Harden section-label widths after any batch (including undo/redo).
      // Section labels derive their width from the viewport, but after undo
      // they may be restored with width=0/undefined. Repair them here to
      // ensure the hr line remains visible.
      const state = store.getState();
      const viewportWidth = state.width;
      const [x, , zoom] = state.transform;
      const zoomFactor = 1 / zoom;
      const scaledWidth = viewportWidth * zoomFactor;
      const scaledX = -(x * zoomFactor);

      const repairedNodes = nextNodes.map((n) => {
        if (!isSectionLabelNode(n)) return n;
        if (n.width && n.width > 0) return n;

        return {
          ...n,
          width: scaledWidth,
          position: { ...n.position, x: scaledX },
        };
      });

      return repairedNodes;
    },
    [
      didRunInit,
      interSectionPadding,
      isInitialized,
      labelPadding,
      padding,
      store,
    ]
  );

  return { applySectionBoundsToNodes, initSectionBounds, didRunInit };
}

/**
 * Hook – useSectionNodesResizeHandlers
 * Converts viewport transforms (x, zoom) into synthetic *node change* events
 * that stretch every section label to be full-width and keep their X aligned.
 */
export function useSectionNodesResizeHandlers() {
  const { getState } = useStoreApi();
  const { setNodes } = useReactFlow();

  const getSectionNodeWidthChanges = useCallback(
    ({ x, zoom }: Viewport) => {
      const nodes = getState().nodes;
      const viewportWidth = getState().width;
      const sectionNodes = nodes.filter(isSectionLabelNode);

      // Compute a width/x that always is the maximum, regardless of scaling
      const zoomFactor = 1 / zoom;
      const scaledWidth = viewportWidth * zoomFactor;
      const scaledX = -(x * zoomFactor);

      const changes: NodeChange[] = sectionNodes.flatMap((n) => {
        const dimensions: NodeDimensionChange = {
          id: n.id,
          type: 'dimensions',
          dimensions: { width: scaledWidth, height: n.height ?? 0 },
          setAttributes: true,
        };

        const nextPosition = { ...n.position, x: scaledX };

        const position: NodePositionChange = {
          id: n.id,
          type: 'position',
          position: { ...nextPosition },
          positionAbsolute: { ...nextPosition },
        };

        return [dimensions, position];
      });

      return changes;
    },
    [getState]
  );

  const onChange = useCallback(
    (vp: Viewport) => {
      const nodes = getState().nodes;
      const sectionNodes = nodes.filter(isSectionLabelNode);
      if (!sectionNodes.length) return; // No update needed

      const triggerNodeChanges = getState().triggerNodeChanges;
      triggerNodeChanges(getSectionNodeWidthChanges(vp));
    },
    [getSectionNodeWidthChanges, getState]
  );

  // Uses setNodes directly to bypass the node change pipeline.
  // This is needed for history restore to avoid triggering isPureViewportWidthBatch.
  const repairSectionNodeWidths = useCallback(() => {
    const { nodes, width, transform } = getState();
    const [x, , zoom] = transform;

    if (!nodes.some(isSectionLabelNode)) return;

    const zoomFactor = 1 / zoom;
    const scaledWidth = width * zoomFactor;
    const scaledX = -(x * zoomFactor);

    setNodes(
      nodes.map((n) => {
        if (!isSectionLabelNode(n)) return n;

        return {
          ...n,
          width: scaledWidth,
          position: { ...n.position, x: scaledX },
        };
      })
    );
  }, [getState, setNodes]);

  const forceSectionNodesRelayout = useCallback(() => {
    const [x, y, zoom] = getState().transform;
    onChange({ x, y, zoom });
  }, [getState, onChange]);

  return {
    forceSectionNodesRelayout,
    repairSectionNodeWidths,
    onViewportChange: onChange,
    getSectionNodeWidthChanges,
  };
}

/**
 * Hook – useSectionNodeResizeListeners
 * Registers listeners so that section labels react to *two* stimuli:
 *   • Continuous panning/zooming via `useOnViewportChange` (X/width only).
 *   • Discrete canvas-size changes (window resize, sidebar toggle).
 */
export function useSectionNodeResizeListeners(
  getState: ExternalFlowChartProps['getState']
) {
  const { onViewportChange, forceSectionNodesRelayout } =
    useSectionNodesResizeHandlers();
  const { canvasDimensions } = useCanvasDimensions();
  const { flowBounds } = useFlowChartContext();

  // Keep section labels flush with the viewport as the user pans / zooms.
  useOnViewportChange({ onChange: onViewportChange });

  // Additionally, when the canvas itself resizes (e.g. window resize or
  // side-panel collapse), we must re-apply the width calculation once.
  useEffect(() => {
    if (getState?.().isInitializing || !getState) return;
    forceSectionNodesRelayout();
  }, [
    forceSectionNodesRelayout,
    canvasDimensions.width,
    flowBounds.width,
    getState,
  ]);
}
